import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import { computeWithholdingFromPercent } from './PaymentAmountsSummaryBar';

const toNumber = (raw) => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    return Number.isFinite(n) ? n : 0;
};

const resolvePercent = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Deduction percent configured on the account card of the client / provider, if any. */
export const readDeductionRate = (accountCard) =>
    resolvePercent(accountCard?.deductionRate ?? accountCard?.DeductionRate);

/**
 * State for the single withholding-tax row of a receipt/payment.
 *
 * The row is visible once the user adds it, and straight away for a document that already
 * carries a deduction. The amount can be typed, or driven by a percent — either the
 * account card deduction rate or one typed by the user — in which case it follows the base
 * it was calculated from.
 */
export const usePaymentWithholdingRow = ({
    withholdingTax,
    deductionRatePercent = null,
    amounts,
    onWithholdingChange,
}) => {
    const [added, setAdded] = useState(false);
    const [percent, setPercent] = useState('');
    const [useDeductionRate, setUseDeductionRate] = useState(false);

    const isVisible = added || toNumber(withholdingTax) > 0;

    const activePercent = useMemo(
        () => (useDeductionRate ? resolvePercent(deductionRatePercent) : resolvePercent(percent)),
        [useDeductionRate, deductionRatePercent, percent]
    );

    const calculatedAmount = useMemo(
        () => computeWithholdingFromPercent({ percent: activePercent, amounts }),
        [activePercent, amounts]
    );

    useEffect(() => {
        if (!isVisible || activePercent == null) return;
        if (Math.abs(calculatedAmount - toNumber(withholdingTax)) < 0.005) return;
        onWithholdingChange(calculatedAmount);
    }, [isVisible, activePercent, calculatedAmount, withholdingTax]);

    const addRow = () => {
        setAdded(true);
        const rate = resolvePercent(deductionRatePercent);
        if (rate == null) return;
        setUseDeductionRate(true);
        setPercent(String(rate));
    };

    const removeRow = () => {
        setAdded(false);
        setUseDeductionRate(false);
        setPercent('');
        onWithholdingChange(0);
    };

    return {
        isVisible,
        addRow,
        rowProps: {
            amount: withholdingTax,
            onAmountChange: (value) => onWithholdingChange(value === '' ? 0 : Number(value)),
            percent,
            onPercentChange: (value) => {
                setUseDeductionRate(false);
                setPercent(value);
            },
            deductionRatePercent: resolvePercent(deductionRatePercent),
            useDeductionRate,
            onUseDeductionRateChange: (checked) => {
                setUseDeductionRate(checked);
                const rate = resolvePercent(deductionRatePercent);
                if (checked && rate != null) {
                    setPercent(String(rate));
                }
            },
            calculatedAmount: activePercent == null ? null : calculatedAmount,
            onRemove: removeRow,
        },
    };
};

/* Same sizing/alignment as normal rows, but tinted so the withholding line stands out. */
const WithholdingCell = styled(TableCell)({
    borderTop: '1px solid rgba(191, 191, 193, 0.56)',
    backgroundColor: 'rgba(249, 170, 42, 0.12)',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    padding: '6px 4px',
    fontSize: '13px',
});

const WithholdingRow = styled(TableRow)({
    '& .MuiTableCell-root': {
        boxShadow: 'inset 0 -1px 0 rgba(249, 170, 42, 0.28)',
    },
    '&:hover .MuiTableCell-root': {
        backgroundColor: 'rgba(249, 170, 42, 0.18)',
    },
});

/**
 * The withholding tax as one row of the payment-items table: the label sits in the type
 * column and the amount and deduction percent line up with the Amount / CurrencyAmount
 * columns of the payment lines.
 */
const PaymentWithholdingTaxRow = ({
    t,
    direction,
    inputPropsStyle,
    inputStyle,
    amount,
    onAmountChange,
    percent,
    onPercentChange,
    deductionRatePercent,
    useDeductionRate,
    onUseDeductionRateChange,
    calculatedAmount,
    onRemove,
    disabled = false,
}) => {
    const isPercentDriven = calculatedAmount != null;

    const startAlign = direction === 'rtl' ? 'right' : 'left';

    return (
        <WithholdingRow>
            {/* Type column — says what the row is, like the payment type of a line */}
            <WithholdingCell sx={{ fontWeight: 600, color: '#9A6100' }}>
                {t('WithholdingAtSource')}
            </WithholdingCell>

            <WithholdingCell colSpan={5} sx={{ textAlign: startAlign }}>
                {deductionRatePercent != null && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!useDeductionRate}
                                onChange={(e) => onUseDeductionRateChange(e.target.checked)}
                                size="small"
                                disabled={disabled}
                            />
                        }
                        label={t('UseSupplierDeductionRate', { rate: deductionRatePercent })}
                        sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': { fontSize: '12px', lineHeight: 1.35 },
                        }}
                    />
                )}
            </WithholdingCell>

            {/* Amount */}
            <WithholdingCell>
                <Tooltip
                    title={isPercentDriven
                        ? t('CalculatedWithholdingAmount', { amount: Number(calculatedAmount).toFixed(2) })
                        : t('WithholdingAtSource')}
                    placement="top"
                >
                    <TextField
                        fullWidth
                        type="number"
                        placeholder={t('Amount')}
                        value={amount || ''}
                        onChange={(e) => onAmountChange(e.target.value)}
                        variant="standard"
                        InputProps={inputPropsStyle}
                        inputProps={inputStyle}
                        disabled={disabled || isPercentDriven}
                    />
                </Tooltip>
            </WithholdingCell>

            {/* Deduction percent */}
            <WithholdingCell>
                <Tooltip title={t('WithholdingPercentage')} placement="top">
                    <TextField
                        fullWidth
                        type="number"
                        placeholder={t('WithholdingPercentage')}
                        value={percent}
                        onChange={(e) => onPercentChange(e.target.value)}
                        variant="standard"
                        InputProps={{
                            ...inputPropsStyle,
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        inputProps={inputStyle}
                        disabled={disabled || useDeductionRate}
                    />
                </Tooltip>
            </WithholdingCell>

            <WithholdingCell colSpan={4} />

            <WithholdingCell>
                {!disabled && (
                    <Tooltip title={t('Delete')} placement="top">
                        <Button
                            style={{ color: 'red', minWidth: '20px' }}
                            onClick={onRemove}
                            size="small"
                        >
                            <DeleteIcon style={{ fontSize: '18px' }} />
                        </Button>
                    </Tooltip>
                )}
            </WithholdingCell>
        </WithholdingRow>
    );
};

export default PaymentWithholdingTaxRow;
